import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_TAGS } from '@/shared/api';
import { createSystemPreset, deleteSystemPreset, fetchSystemPresets, updateSystemPreset } from './api';

/** Query-ключи системных пресетов */
export const systemPresetKeys = {
  all: [API_TAGS.SYSTEM_PRESETS] as const,
  list: (tableName?: string) => [...systemPresetKeys.all, 'list', tableName] as const,
};

/**
 * Системные пресеты таблицы. Набор общий для всех пользователей, поэтому кэш один
 * на приложение и доступен любому авторизованному пользователю.
 *
 * Пустое имя таблицы приходит из разделов, где пресетов нет вовсе (выборки): запрос там не
 * нужен, а без имени таблицы сервер отдал бы пресеты всех таблиц разом.
 */
export const useSystemPresets = (tableName: string) =>
  useQuery({
    queryKey: systemPresetKeys.list(tableName),
    queryFn: () => fetchSystemPresets(tableName),
    enabled: Boolean(tableName),
  });

/** Создание системного пресета; по успеху список перечитывается */
export const useCreateSystemPreset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSystemPreset,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: systemPresetKeys.all }),
  });
};

/** Обновление названия, вида или закрепления системного пресета */
export const useUpdateSystemPreset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateSystemPreset,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: systemPresetKeys.all }),
  });
};

/** Удаление системного пресета */
export const useDeleteSystemPreset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSystemPreset,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: systemPresetKeys.all }),
  });
};
